import React, { FC } from 'react'
import { Controller, useFormContext, FieldValues } from 'react-hook-form'
import clsx from 'clsx'
import { toString } from 'lodash'
import { FormInputLabel } from '../FormInputLabel'
import ReactSelect, { MultiValue, SingleValue } from '../ReactSelect'

export interface FormSelectProps {
  name: string
  label?: string
  options: { value: any; label: any }[]
  required?: boolean
  fullWidth?: boolean
  size?: 'lg' | 'md' | 'sm'
  isMulti?: boolean
  defaultValue?: any
  disabled?: boolean
  onChange?: (value: any) => void | Promise<void>
}

export interface DropdownSelectProps {
  inputId?: string
  options: { value: any; label: any }[]
  isMulti?: boolean
  size?: 'lg' | 'md' | 'sm'
  fullWidth?: boolean
  hasError?: boolean
  value?: any
  disabled?: boolean
  onChange?: (option: SingleValue<any> | MultiValue<any>) => void
  onBlur?: () => void
}

export const DropdownSelect: FC<DropdownSelectProps> = ({
  inputId,
  options,
  isMulti,
  size = 'md',
  fullWidth,
  hasError,
  value,
  disabled,
  onChange,
  onBlur,
  ...selectProps
}) => {
  // Per-`size` height + font, as native Tailwind utilities. The `!` modifier
  // overrides ReactSelect's default control classes (min-h-[38px], text-sm).
  // Menu visibility (formerly a 99999 z-index) now comes from ReactSelect's
  // `alwaysVisible` default.
  const controlClass = {
    lg: hasError ? '!min-h-[40px] !text-lg' : '!min-h-[48px] !text-lg',
    md: hasError ? '!min-h-[36px] !text-base' : '!min-h-[44px] !text-base',
    sm: hasError ? '!min-h-[30px] !text-sm' : '!min-h-[38px] !text-sm',
  }[size]

  return (
    <ReactSelect
      inputId={inputId}
      options={options}
      isMulti={isMulti}
      value={value}
      isDisabled={disabled}
      onChange={onChange}
      onBlur={onBlur}
      className={clsx('z-[15]', fullWidth && 'w-full')}
      classNames={{ control: controlClass }}
      {...selectProps}
    />
  )
}

export const FormSelect: FC<FormSelectProps> = ({
  name,
  label,
  options,
  required = false,
  fullWidth,
  size = 'md',
  isMulti,
  defaultValue,           // optional fallback
  disabled,
  onChange: propsOnChange,
  ...selectProps
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FieldValues>()

  const thisError = errors[name]
  const thisErrorMessage = thisError ? toString(thisError.message) : null

  return (
    <div className="relative my-1">
      <FormInputLabel label={label} inputId={name} forSelect/>

      <Controller
        name={name}
        control={control}
        rules={{ required }}
        defaultValue={defaultValue} // prefer putting this in useForm({ defaultValues })
        render={({ field }) => {
          const currentValue = field.value

          const reactSelectValue = isMulti
            ? options.filter(o => Array.isArray(currentValue) && currentValue.includes(o.value))
            : options.find(o => o.value === currentValue) ?? null

          return (
            <DropdownSelect
              inputId={name}
              options={options}
              isMulti={isMulti}
              size={size}
              fullWidth={fullWidth}
              hasError={!!thisErrorMessage}
              disabled={disabled}
              value={reactSelectValue}
              onChange={(option: SingleValue<any> | MultiValue<any>) => {
                const next =
                  isMulti
                    ? (option as MultiValue<any>).map(o => o.value)
                    : (option as SingleValue<any>)?.value ?? null

                // Update RHF state (marks dirty/touched & can revalidate)
                field.onChange(next)
                if (propsOnChange) propsOnChange(next)
              }}
              onBlur={field.onBlur}
              {...selectProps}
            />
          )
        }}
      />

      {thisErrorMessage && (
        <div
          className={clsx(
            'text-red-500 ml-1 pt-0.5 flex h-2',
            size === 'sm' ? 'text-xs' : 'text-sm'
          )}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-0.5" viewBox="0 0 24 24" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clipRule="evenodd"
            />
          </svg>
          {thisErrorMessage}
        </div>
      )}
    </div>
  )
}
